import { createStore, type StoreApi } from 'zustand';

import {
  resolveHelpRequestForStatement,
  type StatementReferenceHelpRequest,
} from '../model/extractedStatements';
import type { StatementTriageEntryMode, StatementTriageOpenSource } from '../model/statementTriage';

/**
 * Per-message Zustand store for the reference-request wizard.
 *
 * Holds the wizard's transient lifecycle — open flag, phase, the
 * auto-start save flag/error, the submitted-search snapshot
 * (`submittedIndices`, `submittedCitationRequestId`), and the
 * searching-phase completion/timeout flags (`searchComplete`,
 * `searchTimedOut`) — plus the Review-phase help-request drafts
 * (`drafts`) for the same message. All per-`messageSid` and
 * sharing the wizard's lifecycle, so they live in one store with one
 * registry/teardown. Nothing here is persisted — the statement-references
 * API remains the source of truth for citation statuses and help
 * requests.
 *
 * Selectors live alongside the store factory so consumers can pass them
 * directly into `useStore(store, selector)` without re-declaring the
 * draft-aggregation math in every row.
 */

// Soft-limit used by the help-request row UI to gate over-length
// warnings. The backend is the authoritative validator at publish time.
const MAX_HELP_REQUEST_DESCRIPTION_LENGTH = 500;

interface HelpRequestDraftState {
  description: string;
  /** True once the author has typed into this row's input during the
   * current wizard session. Gates server-side hydration so a realtime
   * update of the persisted entry doesn't clobber an active draft. */
  hasBeenTouched: boolean;
}

/**
 * Wizard phase the triage sheet is currently rendering.
 *
 * - `selection` — transient launch state. The statement picker has been
 *   removed; the wizard opens here only briefly while the auto-start
 *   search fires (a spinner shows), or rests here to display an
 *   error + retry if that create fails.
 * - `searching` — the POST has succeeded; the sheet stays mounted and
 *   shows per-statement progress bars for `submittedIndices` while the
 *   backend works.
 * - `review` — the citation request has reached `awaiting_review`. The
 *   sheet renders the proposed-reference review body inline so the
 *   expert can publish without re-opening a second overlay.
 * - `provide` — the expert entered via "I have references in mind"
 *   (`entryMode === 'provide'`). The provide flow opens directly on this
 *   phase, skipping selection: for each extracted statement the expert
 *   supplies one verbatim URL or a describe-it help request, published
 *   directly to the statement-references API. See
 *   `planProvidedStatementReferences`.
 */
export type StatementTriagePhase = 'selection' | 'searching' | 'review' | 'provide';

type StatementTriageState = {
  /** Whether the action sheet is currently open. */
  isOpen: boolean;
  /** Current wizard phase. See `StatementTriagePhase`. */
  phase: StatementTriagePhase;
  /** How the wizard was opened. See `StatementTriageEntryMode`. Set by
   * the trigger before `open()` (native: via the handoff payload; web:
   * via `setEntryMode`). Preserved across open/close like `phase`;
   * reset to `search` by `reset()`. */
  entryMode: StatementTriageEntryMode;
  /** Where the wizard was opened from. See `StatementTriageOpenSource`. Set by
   * the trigger before `open()`; preserved across open/close like `entryMode`;
   * reset to `inline_prompt` by `reset()`. Analytics-only — does not affect the
   * wizard's behavior. */
  openSource: StatementTriageOpenSource;
  /** Statement indices the provide phase renders a row for. Snapshotted
   * by `enterProvide` so the provide phase shows a stable row per
   * statement even if persisted `statementCitationStatuses` change
   * underneath. Empty outside the provide flow. */
  provideStatementIndices: number[];
  /** True while the auto-start create POST is in flight. */
  isSaving: boolean;
  /** Inline error message rendered when a Save throws. Cleared on
   * the next `beginSave`. */
  errorMessage: string | null;
  /** Snapshot of the statement indices submitted to the citation
   * request at send time. Drives the searching-phase row list so it
   * stays stable even if the message's statement references update
   * mid-search. Empty in `selection`. */
  submittedIndices: number[];
  /** Citation request id returned by the successful POST. Used by
   * the hook to subscribe to live status and drive the
   * `searching → review | close` transition. `null` in `selection`. */
  submittedCitationRequestId: string | null;
  /** True once the backend has reported the search complete
   * (`awaiting_review`/`published`) while the wizard was in the
   * `searching` phase. Set synchronously by `markSearchComplete` the
   * instant that signal arrives — before the short settle delay that
   * precedes `enterReview` — so the per-statement progress bars can
   * snap to 100 % while the sheet is still on the searching phase.
   * Reset to `false` by `enterSearching` at the start of each run. */
  searchComplete: boolean;
  /** True once the search has been in the `searching` phase past the
   * wizard's max-wait budget without completing. Set by
   * `markSearchTimedOut`; drives a "taking longer than expected"
   * message so a search whose completion signal never arrives (e.g. a
   * hung workflow) doesn't leave the expert staring at a bar that
   * never fills. Reset to `false` by `enterSearching`. The persistent
   * "Notify me when ready" affordance remains the non-hanging exit. */
  searchTimedOut: boolean;
  /** Review-phase help-request drafts keyed by `statementIndex`.
   * Absent keys mean "no draft" (same as an entry with empty
   * description, except hydration treats untouched entries as eligible
   * for seeding). Drafts persist across `open`/`close`/`reset` — they
   * are local-only until publish and are dropped only when the store
   * is released from the registry on bubble unmount. */
  drafts: Map<number, HelpRequestDraftState>;
};

type StatementTriageActions = {
  /** Opens the action sheet. Clears the in-flight/error transient
   * fields, but **preserves** `phase`, `submittedIndices`, and
   * `submittedCitationRequestId` so a user who closed the sheet
   * mid-wizard re-enters at the same step. Callers that want a fully
   * fresh wizard (e.g. retry-after-cancel) should call `reset()` first. */
  open: () => void;
  /** Closes the action sheet. Clears the in-flight/error transient
   * fields, but **preserves** `phase`, `submittedIndices`, and
   * `submittedCitationRequestId` so reopening the sheet — including
   * after the bubble re-mounts on conversation re-entry — restores
   * the same wizard step. The hook also reconciles those fields from
   * the live citation request, so even if the in-memory store was
   * destroyed (registry cleanup on unmount) the next mount picks the
   * correct phase back up from server state. */
  close: () => void;
  /** Full reset back to the initial state. Used when the wizard's
   * snapshot is no longer meaningful — e.g. the active citation
   * request terminated as `failed`/`cancelled`, or the expert tapped
   * "Retry search" which discards the in-flight request before
   * re-opening the sheet. */
  reset: () => void;
  /** Marks the auto-start create as in-flight and clears any prior
   * error. The dismiss affordances read `isSaving` to disable
   * themselves. */
  beginSave: () => void;
  /** Finalises a save. When `error` is supplied, sets `errorMessage`
   * and leaves pending changes intact so the user can retry. When
   * omitted, the save succeeded and the caller is expected to call
   * `enterSearching` next (or `close` if the workflow flag is off). */
  endSave: (error?: Error) => void;
  /** Transition from `selection` to `searching`. Snapshots the
   * submitted statement indices and the new citation request id, and
   * resets the searching-phase flags (`searchComplete`,
   * `searchTimedOut`) so a re-run starts fresh. Clears isSaving. */
  enterSearching: (indices: readonly number[], citationRequestId: string) => void;
  /** Set the wizard's entry mode. Called by the trigger before `open()`
   * so the wizard knows whether it runs a search (`search`) or opens the
   * provide phase (`provide`). */
  setEntryMode: (mode: StatementTriageEntryMode) => void;
  /** Set where the wizard was opened from, for the OPENED analytics event.
   * Called by the trigger before `open()`. */
  setOpenSource: (source: StatementTriageOpenSource) => void;
  /** Open the provide phase ("I have references in mind"). Snapshots the
   * statement indices into `provideStatementIndices` so the provide
   * phase renders one stable row per statement. Runs no search and
   * creates no citation request. Clears isSaving / errorMessage. */
  enterProvide: (indices: readonly number[]) => void;
  /** Transition from `searching` to `review`. Used by the hook when
   * the live citation request transitions to `awaiting_review`. */
  enterReview: () => void;
  /** Mark the search complete so the searching-phase progress bars snap
   * to 100 %. Called by the hook the instant the backend reports
   * `awaiting_review`/`published`, ahead of the short settle delay
   * before `enterReview`. */
  markSearchComplete: () => void;
  /** Mark the search as having exceeded the wizard's max-wait budget
   * without a completion signal. Drives the "taking longer than
   * expected" copy in the searching phase. */
  markSearchTimedOut: () => void;
  /** Replace the help-request draft for the given statement and mark
   * it touched. */
  setDescriptionDraft: (statementIndex: number, next: string) => void;
  /** Seed a help-request draft from the persisted entry on first
   * render of a row. No-op if the row has already been touched by the
   * user, if the current draft already matches the persisted value, or
   * if the persisted value is empty (rows mount unconditionally even
   * when no help-request exists yet — seeding empty would create
   * useless Map entries on every render). */
  hydrateFromPersisted: (statementIndex: number, description: string) => void;
  /** Test-only: drop a single statement's help-request draft entry.
   * The double-underscore matches the convention used by
   * `__resetStatementTriageStoreRegistry` and signals this is not part
   * of the production action surface. */
  __clearDraft: (statementIndex: number) => void;
};

export type StatementTriageStore = StatementTriageState & StatementTriageActions;

/* ---------- Help-request draft selectors ------------------------------- */

/** The current help-request draft text for the given statement, or `''`
 * when no draft exists. */
export function selectDraftText(state: StatementTriageStore, statementIndex: number): string {
  return state.drafts.get(statementIndex)?.description ?? '';
}

/**
 * Returns the set of statement indices whose trimmed help-request draft
 * is non-empty — i.e. the statements that would publish a help-request
 * entry if the expert tapped Publish right now.
 */
export function selectStatementIndicesWithDraft(state: StatementTriageStore): Set<number> {
  const result = new Set<number>();

  for (const [statementIndex, draft] of state.drafts) {
    if (draft.description.trim().length > 0) {
      result.add(statementIndex);
    }
  }

  return result;
}

/** Total non-empty help-request drafts across all rows. Drives the publish-CTA count. */
export function selectDraftCount(state: StatementTriageStore): number {
  return selectStatementIndicesWithDraft(state).size;
}

/**
 * True when the local help-request drafts differ from the persisted
 * help-request array in a way that a Publish would push to the server.
 * Compared within `universeStatementIndices` so out-of-scope persisted
 * entries are ignored.
 *
 * Specifically:
 * - Any non-empty draft whose trimmed text differs from the persisted
 *   description for the same statement (insert OR update).
 * - Any persisted entry within the universe that does not have a
 *   matching non-empty draft (remove).
 */
export function selectHasUnpublishedDraftChanges(
  state: StatementTriageStore,
  persisted: readonly StatementReferenceHelpRequest[] | undefined,
  universeStatementIndices: readonly number[]
): boolean {
  for (const statementIndex of universeStatementIndices) {
    const draftEntry = state.drafts.get(statementIndex);

    const trimmedDraft = (draftEntry?.description ?? '').trim();

    // Over-limit drafts are treated as empty by `buildHelpRequestNext`
    // — they remove the persisted entry rather than creating a new one.
    // Mirror that behavior here so the Publish CTA accurately reflects
    // what a publish would do (remove vs. no-op).
    const effectiveDraft =
      trimmedDraft.length > MAX_HELP_REQUEST_DESCRIPTION_LENGTH ? '' : trimmedDraft;

    const persistedEntry = resolveHelpRequestForStatement(persisted, statementIndex);

    if (effectiveDraft.length === 0) {
      if (persistedEntry) {
        return true;
      }

      continue;
    }

    if (!persistedEntry) {
      return true;
    }

    if (persistedEntry.description !== effectiveDraft) {
      return true;
    }
  }

  return false;
}

const INITIAL_STATE: StatementTriageState = {
  isOpen: false,
  phase: 'selection',
  entryMode: 'search',
  openSource: 'inline_prompt',
  provideStatementIndices: [],
  isSaving: false,
  errorMessage: null,
  submittedIndices: [],
  submittedCitationRequestId: null,
  searchComplete: false,
  searchTimedOut: false,
  drafts: new Map(),
};

/**
 * Transient UI fields cleared on both `open()` and `close()` so the
 * wizard never reopens with stale per-session input. The wizard
 * snapshot fields (`phase`, `submittedIndices`,
 * `submittedCitationRequestId`) are deliberately omitted — see the
 * `open` / `close` docstrings.
 */
function freshTransientFields(): Pick<StatementTriageState, 'isSaving' | 'errorMessage'> {
  return { isSaving: false, errorMessage: null };
}

export function createStatementTriageStore(): StoreApi<StatementTriageStore> {
  return createStore<StatementTriageStore>((set) => ({
    ...INITIAL_STATE,

    open: () => set({ isOpen: true, ...freshTransientFields() }),

    close: () => set({ isOpen: false, ...freshTransientFields() }),

    // Drafts persist across reset — they are local-only help-request
    // input that should survive the wizard snapshot being discarded
    // (e.g. on a terminal status). Only registry release drops them.
    reset: () => set((state) => ({ ...INITIAL_STATE, drafts: state.drafts })),

    beginSave: () => set({ isSaving: true, errorMessage: null }),

    endSave: (error) =>
      set({
        isSaving: false,
        errorMessage: error ? error.message : null,
      }),

    enterSearching: (indices, citationRequestId) =>
      set({
        phase: 'searching',
        submittedIndices: [...indices],
        submittedCitationRequestId: citationRequestId,
        searchComplete: false,
        searchTimedOut: false,
        isSaving: false,
        errorMessage: null,
      }),

    setEntryMode: (mode) => set({ entryMode: mode }),

    setOpenSource: (source) => set({ openSource: source }),

    enterProvide: (indices) =>
      set({
        phase: 'provide',
        provideStatementIndices: [...indices],
        isSaving: false,
        errorMessage: null,
      }),

    enterReview: () => set({ phase: 'review' }),

    // Clear searchTimedOut too: a completion that lands after the
    // max-wait timeout must win, so the searching blurb can't keep
    // showing "taking longer than usual" while the bars read 100 %.
    markSearchComplete: () => set({ searchComplete: true, searchTimedOut: false }),

    markSearchTimedOut: () => set({ searchTimedOut: true }),

    setDescriptionDraft: (statementIndex, next) =>
      set((state) => {
        const drafts = new Map(state.drafts);

        drafts.set(statementIndex, { description: next, hasBeenTouched: true });

        return { drafts };
      }),

    hydrateFromPersisted: (statementIndex, description) =>
      set((state) => {
        const existing = state.drafts.get(statementIndex);

        if (existing?.hasBeenTouched) {
          return state;
        }

        if (existing?.description === description) {
          return state;
        }

        if (!existing && description.length === 0) {
          // Avoid creating a `{ description: '', hasBeenTouched: false }`
          // entry for every mounted row that has no persisted help
          // request — `drafts.size` should reflect actual content, not
          // mount count.
          return state;
        }

        const drafts = new Map(state.drafts);

        drafts.set(statementIndex, { description, hasBeenTouched: false });

        return { drafts };
      }),

    __clearDraft: (statementIndex) =>
      set((state) => {
        if (!state.drafts.has(statementIndex)) {
          return state;
        }

        const drafts = new Map(state.drafts);

        drafts.delete(statementIndex);

        return { drafts };
      }),
  }));
}

const storeMap = new Map<string, StoreApi<StatementTriageStore>>();

export function getStatementTriageStore(messageSid: string): StoreApi<StatementTriageStore> {
  let store = storeMap.get(messageSid);

  if (!store) {
    store = createStatementTriageStore();

    storeMap.set(messageSid, store);
  }

  return store;
}

export function deleteStatementTriageStore(messageSid: string): void {
  storeMap.delete(messageSid);
}

/** Test-only: clear the registry between tests. */
export function __resetStatementTriageStoreRegistry(): void {
  storeMap.clear();
}
